import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Box, CircularProgress, Typography } from '@mui/material';

// Components
import Navigation from './components/Navigation';
import RecipeGenerator from './components/RecipeGenerator';
import MealPlanner from './components/MealPlanner';
import MealCalendar from './components/MealCalendar';
import Home from './components/Home';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import Profile from './components/Profile';
import Terms from './components/pages/Terms';
import Privacy from './components/pages/Privacy';
import Contact from './components/pages/Contact';
import { useSupabaseAuth } from './hooks/useSupabaseAuth';
import { supabase } from './supabase/config'; // Fixed import path

// Theme with psychology-informed colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#4CAF50', // Green - associated with freshness, health
    },
    secondary: {
      main: '#FF9800', // Orange - stimulates appetite
    },
    background: {
      default: '#f7f9fc', // Light blue-gray - calm, clean
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useSupabaseAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Auth callback handler component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Wait for Supabase to finish processing the OAuth callback
    const handleCallback = async () => {
      try {
        // Check the URL for authentication fragments or query parameters
        const hashParams = new URLSearchParams(location.hash.replace('#', '?'));
        const queryParams = new URLSearchParams(location.search);
        
        // If we have authentication parameters, the auth process is complete
        if (hashParams.has('access_token') || queryParams.has('access_token')) {
          // Let Supabase handle the rest internally
          await supabase.auth.getSession();
          
          // Navigate back to home page
          navigate('/', { replace: true });
          toast.success('Successfully signed in!');
        } else {
          // If no auth params found, redirect to login
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Error processing auth callback:', error);
        navigate('/login', { replace: true });
        toast.error('Authentication failed. Please try again.');
      }
    };
    
    handleCallback();
  }, [navigate, location]);
  
  // Show a loading indicator while processing
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
      <Typography variant="h6" sx={{ ml: 2 }}>
        Completing authentication...
      </Typography>
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Auth callback route */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* All other routes with Navigation */}
          <Route path="*" element={<MainLayout />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

// Main layout component with navigation
const MainLayout = () => {
  return (
    <>
      <Navigation />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/recipe-generator" element={<RecipeGenerator />} />
        <Route path="/meal-planner" element={<MealPlanner />} />
        <Route path="/meal-calendar" element={
          <ProtectedRoute>
            <MealCalendar />
          </ProtectedRoute>
        } />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        
        {/* New routes for Terms, Privacy, and Contact pages */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />
        
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

export default App;
